import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { UPDATE_PURCHASE_ORDER_DATES_KEY } from '@/keys';
import { useSendPurchaseOrder } from '@/services/apis/mutations/purchase-orders/use-send-purchase-order';
import { useGetPurchaseOrder } from '@/services/apis/queries/purchase-order/use-get-purchase-order';
import type { PurchaseOrder } from '@/types/core/purchase-order/purchase-order';
import {
  getMissingPurchaseOrderDates,
  getPurchaseOrderDates,
  purchaseOrderDatesSchema,
} from '../schema/purchase-order-dates-schema';

type UsePurchaseOrderSendButtonProps = {
  purchaseOrderId: string;
};

function isSendingStatus(purchaseOrder: PurchaseOrder) {
  return (
    purchaseOrder.statusCode === 'ORDER_CREATION' ||
    purchaseOrder.statusCode === 'WAITING_FOR_SUPPLIER_AGREEMENT'
  );
}

function getSendingDateProblem(
  purchaseOrder: PurchaseOrder,
  t: TFunction<'purchaseOrder'>,
) {
  const missingDates = getMissingPurchaseOrderDates(purchaseOrder);
  if (missingDates.length > 0) {
    return t('sendPoMissingDates', { dates: missingDates.join(', ') });
  }
  const validation = purchaseOrderDatesSchema.safeParse(
    getPurchaseOrderDates(purchaseOrder),
  );
  const issue = validation.success ? undefined : validation.error.issues[0];
  return issue
    ? t(issue.message, issue.code === 'custom' ? issue.params : undefined)
    : null;
}

export function usePurchaseOrderSendButton({
  purchaseOrderId,
}: UsePurchaseOrderSendButtonProps) {
  const { t } = useTranslation('purchaseOrder');
  const { data: purchaseOrder, refetch } = useGetPurchaseOrder(purchaseOrderId);
  const { mutateAsync: sendPurchaseOrder, isPending } = useSendPurchaseOrder();
  const [isCheckingPurchaseOrder, setIsCheckingPurchaseOrder] = useState(false);
  const queryClient = useQueryClient();
  const updatingDatesCount = useIsMutating({
    mutationKey: [...UPDATE_PURCHASE_ORDER_DATES_KEY, purchaseOrderId],
  });

  const canSendPurchaseOrder = isSendingStatus(purchaseOrder);
  const sendingBlockedReason =
    getSendingDateProblem(purchaseOrder, t) ??
    (updatingDatesCount > 0 ? t('sendPoSavingDates') : null);

  const initialRecipientLabels: Record<string, string> = {};
  const initialRecipientEmails = (purchaseOrder.supplier.contacts ?? [])
    .map((contact) => {
      const email = contact.email?.trim();
      const name = contact.name?.trim();
      if (!email) {
        return null;
      }
      if (name) {
        initialRecipientLabels[email] = name;
      }
      return email;
    })
    .filter((email): email is string => Boolean(email));

  const handleSendPurchaseOrder = async (
    params: { recipients: string[]; message: string; warehouseId: number },
    onSuccess?: () => void,
  ) => {
    if (sendingBlockedReason || isPending || isCheckingPurchaseOrder) {
      return;
    }

    setIsCheckingPurchaseOrder(true);
    try {
      // The dialog may have stayed open while the PO changed elsewhere.
      const { data: currentPurchaseOrder } = await refetch({
        throwOnError: true,
      });
      if (!currentPurchaseOrder) {
        throw new Error('Purchase order could not be refreshed.');
      }
      if (
        queryClient.isMutating({
          mutationKey: [...UPDATE_PURCHASE_ORDER_DATES_KEY, purchaseOrderId],
        }) > 0
      ) {
        toast.error(t('sendPoSavingDates'));
        return;
      }
      const currentDateProblem = getSendingDateProblem(currentPurchaseOrder, t);
      if (currentDateProblem) {
        toast.error(currentDateProblem);
        return;
      }
      if (!isSendingStatus(currentPurchaseOrder)) {
        toast.error(t('sendPoError'));
        return;
      }

      await sendPurchaseOrder({ id: purchaseOrderId, ...params });
      toast.success(t('sendPoSuccess'));
      onSuccess?.();
    } catch {
      toast.error(t('error', { ns: 'common' }), {
        description: t('sendPoError'),
      });
      // Also reflect a concurrent server-side rejection in the open dialog.
      await refetch();
    } finally {
      setIsCheckingPurchaseOrder(false);
    }
  };

  return {
    availableWarehouses: purchaseOrder.availableWarehouses,
    canSendPurchaseOrder,
    handleSendPurchaseOrder,
    isSendingPurchaseOrder: isPending || isCheckingPurchaseOrder,
    sendingBlockedReason,
    initialRecipientEmails,
    initialRecipientLabels,
    selectedWarehouse: purchaseOrder.warehouse,
  };
}
